import { computed, ComputedRef, ref } from 'vue';
import { JOINT_ANGLE_MAX, JOINT_ANGLE_MIN } from '@/modules/ConfiguratorJoints/composables/useShapeAndAngle';

/**
 * Step 4 state: corner guide + joint type.
 *
 * The available options are derived from the previous steps' choices (the
 * bend angle from step 3 and, for the corner type, the guide chosen here).
 * The deriving logic itself lives in `GuidesAndType.vue` so it sits next to
 * the dropdowns it feeds — this composable only owns the selected values.
 */
export type GuideType = 'wire-guide' | 'inox-guide' | 'alu-guide' | 'no-guide' | 'concealed-guide';
export type CornerType = 'type-1' | 'type-2';
export type GuideArmPosition = 1 | 2;
export type GuideCorner = 'leftCorner' | 'rightCorner';
export type IndividualGuideTypes = Record<GuideArmPosition, Record<GuideCorner, GuideType | null>>;

export interface OptionAvailability {
    minAngle: number;
    maxAngle: number;
    requiredCornerTypes: CornerType[];
}

export interface ConfigurableOption<TValue extends string> {
    value: TValue;
    label: string;
    availability: OptionAvailability;
    previewImage?: string;
    checklistMappedValue?: string;
}

export interface GuideTypeOption extends ConfigurableOption<GuideType> {
    previewImage: string;
}

export interface GuidesAndTypeConfig {
    guideTypes: GuideTypeOption[];
    cornerTypes: ConfigurableOption<CornerType>[];
}

/** Type 2 is always available, so it is the safe default selection. */
export const DEFAULT_CORNER_TYPE: CornerType = 'type-2';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const regularGuidePreviewUrl: string = require('@/modules/ConfiguratorJoints/assets/alu-guide-top.svg');
// eslint-disable-next-line @typescript-eslint/no-var-requires
const regularGuideHeroTwoPreviewUrl: string = require('@/modules/ConfiguratorJoints/assets/hero-2-alu-guide-top.png');
// eslint-disable-next-line @typescript-eslint/no-var-requires
const regularGuideHeroFivePreviewUrl: string = require('@/modules/ConfiguratorJoints/assets/hero-5-alu-guide-top.png');
// eslint-disable-next-line @typescript-eslint/no-var-requires
const wireGuidePreviewUrl: string = require('@/modules/ConfiguratorJoints/assets/wire-guide.svg');
// eslint-disable-next-line @typescript-eslint/no-var-requires
const inoxGuidePreviewUrl: string = require('@/modules/ConfiguratorJoints/assets/inox-guide.svg');
// eslint-disable-next-line @typescript-eslint/no-var-requires
const slatConnectorPreviewUrl: string = require('@/modules/ConfiguratorJoints/assets/slat-connector.png');
// eslint-disable-next-line @typescript-eslint/no-var-requires
const cornerTypeOneImage: string = require('@/modules/ConfiguratorJoints/assets/corner-type-1.png');
// eslint-disable-next-line @typescript-eslint/no-var-requires
const cornerTypeTwoImage: string = require('@/modules/ConfiguratorJoints/assets/corner-type-2.png');
// eslint-disable-next-line @typescript-eslint/no-var-requires
const concealedGuidePreviewUrl: string = require('@/modules/ConfiguratorJoints/assets/hero-3-concealed-guide-top.png');

const guidesAndTypeConfigBase: GuidesAndTypeConfig = {
    guideTypes: [
        {
            value: 'wire-guide',
            label: 'Wire guides',
            previewImage: wireGuidePreviewUrl,
            availability: { minAngle: 45, maxAngle: 90, requiredCornerTypes: ['type-1', 'type-2'] },
        },
        {
            value: 'inox-guide',
            label: 'Inox guides',
            previewImage: inoxGuidePreviewUrl,
            availability: { minAngle: 45, maxAngle: 90, requiredCornerTypes: ['type-1', 'type-2'] },
        },
        {
            value: 'alu-guide',
            label: 'Alu guides',
            previewImage: regularGuidePreviewUrl,
            availability: { minAngle: 45, maxAngle: 90, requiredCornerTypes: ['type-2'] },
        },
        {
            value: 'no-guide',
            label: 'No guides',
            previewImage: slatConnectorPreviewUrl,
            availability: { minAngle: 75, maxAngle: 90, requiredCornerTypes: ['type-2'] },
        },
        {
            value: 'concealed-guide',
            label: 'Concealed guides',
            previewImage: concealedGuidePreviewUrl,
            availability: { minAngle: 45, maxAngle: 90, requiredCornerTypes: ['type-2'] },
        },
    ],
    cornerTypes: [
        {
            value: 'type-1',
            label: 'Type 1',
            previewImage: cornerTypeOneImage,
            availability: { minAngle: 90, maxAngle: 90, requiredCornerTypes: [] },
        },
        {
            value: 'type-2',
            label: 'Type 2',
            previewImage: cornerTypeTwoImage,
            availability: { minAngle: JOINT_ANGLE_MIN, maxAngle: JOINT_ANGLE_MAX, requiredCornerTypes: [] },
        },
    ],
};

function getGuidePreviewUrl(guideType: GuideType, configuratorType: string): string {
    switch (configuratorType) {
        case 'hero-2':
            return regularGuideHeroTwoPreviewUrl;
        case 'hero-5':
            return regularGuideHeroFivePreviewUrl;
        default:
            return regularGuidePreviewUrl;
    }
}

const DEFAULT_ARM_POSITIONS: GuideArmPosition[] = [1, 2];
const GUIDE_CORNERS: GuideCorner[] = ['leftCorner', 'rightCorner'];

function createEmptyIndividualGuideTypes(): IndividualGuideTypes {
    return {
        1: {
            leftCorner: null,
            rightCorner: null,
        },
        2: {
            leftCorner: null,
            rightCorner: null,
        },
    };
}

export function useGuidesAndType(currentConfig: ComputedRef<any>) {
    const guideType = ref<GuideType | null>(null);
    const individualGuidesEnabled = ref<boolean>(false);
    const individualGuideTypes = ref<IndividualGuideTypes>(createEmptyIndividualGuideTypes());
    const cornerType = ref<CornerType | null>(null);

    const guidesAndTypeConfig = computed<GuidesAndTypeConfig>(() => {
        const aluGuidePreviewUrl = getGuidePreviewUrl('alu-guide', currentConfig.value?.configuratorType ?? 'default');
        const guideTypeField = currentConfig.value?.checklistFields?.find((field: any) => field.key === 'guideType');
        const allowedGuides: GuideType[] = guideTypeField?.options?.map((o: any) => o.label as GuideType) ?? [];

        return {
            ...guidesAndTypeConfigBase,
            guideTypes: guidesAndTypeConfigBase.guideTypes
                .filter((option) => allowedGuides.includes(option.value))
                .map((option) =>
                    option.value === 'alu-guide' ? { ...option, previewImage: aluGuidePreviewUrl } : option,
                ),
        };
    });

    function getGuideTypeChecklistValue(guideType: GuideType | null): string {
        if (!guideType) {
            return '';
        }

        const options: Array<{ label: string; value: string }> =
            currentConfig.value?.checklistFields?.find((field: any) => field.key === 'guideType')?.options ?? [];
        return options.find((o) => o.label === guideType)?.value ?? '';
    }

    const areGuidesConfigured = computed<boolean>(() =>
        DEFAULT_ARM_POSITIONS.every((position) =>
            GUIDE_CORNERS.every((corner) => individualGuideTypes.value[position][corner] !== null),
        ),
    );

    // Selecting global guide type reset the individual guide types to match the global selection
    function setAllGuideTypes(value: GuideType | null) {
        guideType.value = value;

        if (value === 'no-guide') {
            individualGuideTypes.value = {
                1: {
                    leftCorner: 'alu-guide',
                    rightCorner: value,
                },
                2: {
                    leftCorner: value,
                    rightCorner: 'alu-guide',
                },
            };

            return;
        }

        individualGuideTypes.value = {
            1: {
                leftCorner: value,
                rightCorner: value,
            },
            2: {
                leftCorner: value,
                rightCorner: value,
            },
        };
    }

    // Individual guide type management functions
    function setIndividualGuideType(position: GuideArmPosition, corner: GuideCorner, value: GuideType | null) {
        guideType.value = null;

        individualGuideTypes.value[position][corner] = value;

        // If one inner guide changes to no guide, we change both inner guides since connector requires both arms connected
        if (areWeSettingInnerGuidesToNoGuide(position, corner, value)) {
            setInnerGuidesToNoGuide();
            return;
        }

        // This prevents having one inner guide set to a guide and the other set to no guide,
        // which is not allowed
        if (
            areWeSettingInnerGuidesToAGuide(position, corner, value) &&
            isOtherInnerGuideSetToNoGuide(position, corner, value)
        ) {
            setOtherInnerGuideToSelectedValue(position, corner, value);
            return;
        }
    }

    function isOtherInnerGuideSetToNoGuide(position: GuideArmPosition, corner: GuideCorner, value: GuideType | null) {
        const otherPosition = position === 1 ? 2 : 1;
        const otherCorner = corner === 'leftCorner' ? 'rightCorner' : 'leftCorner';

        return individualGuideTypes.value[otherPosition][otherCorner] === 'no-guide';
    }

    function setOtherInnerGuideToSelectedValue(
        position: GuideArmPosition,
        corner: GuideCorner,
        value: GuideType | null,
    ) {
        const otherPosition = position === 1 ? 2 : 1;
        const otherCorner = corner === 'leftCorner' ? 'rightCorner' : 'leftCorner';

        individualGuideTypes.value[otherPosition][otherCorner] = value;
    }

    function areWeSettingInnerGuidesToNoGuide(
        position: GuideArmPosition,
        corner: GuideCorner,
        value: GuideType | null,
    ) {
        return (
            (position === 1 && corner === 'rightCorner' && value === 'no-guide') ||
            (position === 2 && corner === 'leftCorner' && value === 'no-guide')
        );
    }

    function areWeSettingInnerGuidesToAGuide(position: GuideArmPosition, corner: GuideCorner, value: GuideType | null) {
        return (
            (position === 1 && corner === 'rightCorner' && value !== 'no-guide') ||
            (position === 2 && corner === 'leftCorner' && value !== 'no-guide')
        );
    }

    function setInnerGuidesToNoGuide() {
        individualGuideTypes.value[1]['rightCorner'] = 'no-guide';
        individualGuideTypes.value[2]['leftCorner'] = 'no-guide';
    }

    function areAllGuidesOfType(guideTypeToCheck: GuideType): boolean {
        return DEFAULT_ARM_POSITIONS.every((position) =>
            GUIDE_CORNERS.every((corner) => individualGuideTypes.value[position][corner] === guideTypeToCheck),
        );
    }

    // Global state managemenet functions
    function resetGuides() {
        guideType.value = null;
        individualGuidesEnabled.value = false;
        individualGuideTypes.value = createEmptyIndividualGuideTypes();
    }

    function setIndividualGuidesEnabled(value: boolean) {
        if (!value) {
            resetGuides();
            return;
        }

        individualGuidesEnabled.value = true;
    }

    function setCornerType(value: CornerType) {
        cornerType.value = value;
    }

    function resetGuidesAndType() {
        resetGuides();
        cornerType.value = null;
    }

    return {
        // State
        guideType,
        individualGuidesEnabled,
        individualGuideTypes,
        cornerType,
        // Config
        guidesAndTypeConfig,
        getGuideTypeChecklistValue,
        // Completion
        areGuidesConfigured,
        // Mutations
        setAllGuideTypes,
        setIndividualGuideType,
        setIndividualGuidesEnabled,
        resetGuides,
        setCornerType,
        resetGuidesAndType,
        areAllGuidesOfType,
    };
}
